import { FC, useMemo } from 'react';
import { Alert, Box, Typography, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AIModelSelectionType } from '../../../../context/MannequinContext';
import { isLcwUpscaleHangerModel, supportsHangerCardQualitySetting } from '../../AIGeneration.helper';
import './HangerCardOutputConfig.scss';

type SelectOption = {
    value: string;
    label: string;
};

interface HangerCardOutputConfigProps {
    variant?: 'inline' | 'popover';
    selectedModel: AIModelSelectionType;
    resolution: string;
    aspectRatio: string;
    outputFormat: string;
    outputFormatChangeHint?: string;
    quality?: string;
    outputFormatDisabled?: boolean;
    onResolutionChange: (value: string) => void;
    onAspectRatioChange: (value: string) => void;
    onOutputFormatChange: (value: string) => void;
    onQualityChange?: (value: string) => void;
}

const GEMINI_RESOLUTION_OPTIONS: SelectOption[] = [
    { value: '1K', label: '1K' },
    { value: '2K', label: '2K' },
    { value: '4K', label: '4K' },
];

const LCW_UPSCALE_RESOLUTION_OPTIONS: SelectOption[] = [{ value: '4K', label: '4K' }];

const AZURE_RESOLUTION_OPTIONS: SelectOption[] = [
    { value: '2048x2048', label: '1:1' },
    { value: '2160x3840', label: '9:16' },
    { value: '3840x2160', label: '16:9' },
];

const ASPECT_RATIO_OPTIONS: SelectOption[] = [
    { value: '1:1', label: '1:1' },
    { value: '16:9', label: '16:9' },
    { value: '9:16', label: '9:16' },
    { value: '4:3', label: '4:3' },
    { value: '3:4', label: '3:4' },
];

const OUTPUT_FORMAT_OPTIONS: SelectOption[] = [
    { value: 'jpeg', label: 'JPEG' },
    { value: 'png', label: 'PNG' },
];

const QUALITY_OPTIONS: SelectOption[] = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
];

const isAzureLikeModel = (model: AIModelSelectionType): boolean =>
    model === 'AzureOpenAIGpt' || model === 'AIGate';

const AspectRatioIcon: FC<{ ratio: string }> = ({ ratio }) => {
    const [widthRatio, heightRatio] = ratio.split(':').map(Number);
    const frameSize = 28;
    const maxIconSize = 22;

    let width = maxIconSize;
    let height = maxIconSize;

    if (widthRatio > heightRatio) {
        width = maxIconSize;
        height = Math.max(8, Math.round((maxIconSize * heightRatio) / widthRatio));
    } else if (heightRatio > widthRatio) {
        height = maxIconSize;
        width = Math.max(8, Math.round((maxIconSize * widthRatio) / heightRatio));
    }

    return (
        <span className="hanger-output-config__aspect-icon-wrap" style={{ width: frameSize, height: frameSize }} aria-hidden="true">
            <span className="hanger-output-config__ratio-icon" style={{ width, height }} />
        </span>
    );
};

export const HangerCardOutputConfig: FC<HangerCardOutputConfigProps> = ({
    variant = 'inline',
    selectedModel,
    resolution,
    aspectRatio,
    outputFormat,
    outputFormatChangeHint,
    quality = 'high',
    outputFormatDisabled = false,
    onResolutionChange,
    onAspectRatioChange,
    onOutputFormatChange,
    onQualityChange,
}) => {
    const { t } = useTranslation();
    const isAzureModel = isAzureLikeModel(selectedModel);
    const showsQualitySetting = supportsHangerCardQualitySetting(selectedModel);

    const resolutionOptions = useMemo(() => {
        if (isAzureModel) {
            return AZURE_RESOLUTION_OPTIONS;
        }

        if (isLcwUpscaleHangerModel(selectedModel)) {
            return LCW_UPSCALE_RESOLUTION_OPTIONS;
        }

        return GEMINI_RESOLUTION_OPTIONS;
    }, [isAzureModel, selectedModel]);

    const handleResolutionChange = (_: unknown, value: string | null) => {
        if (!value) {
            return;
        }

        onResolutionChange(value);

        if (isAzureModel) {
            if (value === '2048x2048') onAspectRatioChange('1:1');
            else if (value === '2160x3840') onAspectRatioChange('9:16');
            else if (value === '3840x2160') onAspectRatioChange('16:9');
        }
    };

    const handleOutputFormatChange = (_: unknown, value: string | null) => {
        if (value) {
            onOutputFormatChange(value);
        }
    };

    const handleQualityChange = (_: unknown, value: string | null) => {
        if (value && onQualityChange) {
            onQualityChange(value);
        }
    };

    return (
        <Box className={`hanger-output-config ${variant === 'popover' ? 'hanger-output-config--popover' : ''}`}>
            {variant === 'inline' && (
                <Typography variant="subtitle2" className="hanger-output-config__title">
                    {t('mannequin.aiGeneration.configuration.outputSettings.title', 'Output')}
                </Typography>
            )}

            {!isAzureModel && (
                <Box className="hanger-output-config__section">
                    <Typography variant="caption" className="hanger-output-config__label">
                        {t('mannequin.aiGeneration.configuration.aspectRatio')}
                    </Typography>
                    <Box className="hanger-output-config__aspect-grid" role="group" aria-label={t('mannequin.aiGeneration.configuration.aspectRatio')}>
                        {ASPECT_RATIO_OPTIONS.map((option) => {
                            const selected = aspectRatio === option.value;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`hanger-output-config__aspect-item ${selected ? 'is-selected' : ''}`}
                                    onClick={() => onAspectRatioChange(option.value)}
                                    aria-pressed={selected}
                                >
                                    <AspectRatioIcon ratio={option.value} />
                                    <span>{option.label}</span>
                                </button>
                            );
                        })}
                    </Box>
                </Box>
            )}

            <Box className="hanger-output-config__section">
                <Typography variant="caption" className="hanger-output-config__label">
                    {isAzureModel
                        ? `${t('mannequin.aiGeneration.configuration.aspectRatio')} / ${t('mannequin.aiGeneration.configuration.resolution')}`
                        : t('mannequin.aiGeneration.configuration.resolution')}
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={resolution}
                    onChange={handleResolutionChange}
                    className="hanger-output-config__segment"
                >
                    {resolutionOptions.map((option) => (
                        <ToggleButton key={option.value} value={option.value} className="hanger-output-config__segment-btn">
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            <Box className="hanger-output-config__section hanger-output-config__section--inline">
                <Typography variant="caption" className="hanger-output-config__label">
                    {t('mannequin.aiGeneration.configuration.outputFormat')}
                </Typography>
                <Tooltip
                    title={
                        outputFormatDisabled
                            ? t('mannequin.aiGeneration.hangerFlow.uploadGenerate.outputFormatDisabledTooltip')
                            : ''
                    }
                    placement="top"
                    arrow
                >
                    <Box>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={outputFormat || 'jpeg'}
                            onChange={handleOutputFormatChange}
                            className="hanger-output-config__segment"
                            disabled={outputFormatDisabled}
                        >
                            {OUTPUT_FORMAT_OPTIONS.map((option) => (
                                <ToggleButton
                                    key={option.value}
                                    value={option.value}
                                    className="hanger-output-config__segment-btn"
                                >
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                </Tooltip>
                {outputFormatChangeHint && (
                    <Alert severity="info" className="hanger-output-config__format-hint">
                        {outputFormatChangeHint}
                    </Alert>
                )}
            </Box>

            {showsQualitySetting && onQualityChange && (
                <Box className="hanger-output-config__section hanger-output-config__section--inline">
                    <Typography variant="caption" className="hanger-output-config__label">
                        {t('mannequin.aiGeneration.configuration.quality', 'Kalite')}
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={quality}
                        onChange={handleQualityChange}
                        className="hanger-output-config__segment"
                    >
                        {QUALITY_OPTIONS.map((option) => (
                            <ToggleButton key={option.value} value={option.value} className="hanger-output-config__segment-btn">
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            )}
        </Box>
    );
};

export const getHangerCardOutputSummary = (
    selectedModel: AIModelSelectionType,
    aspectRatio: string,
    resolution: string
): string => {
    if (isAzureLikeModel(selectedModel)) {
        const azureOption = AZURE_RESOLUTION_OPTIONS.find((option) => option.value === resolution);
        return azureOption ? `${azureOption.label} · ${resolution}` : resolution;
    }

    return `${aspectRatio} · ${resolution}`;
};
